import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GitMerge, RefreshCw, X } from "lucide-react";
import { API } from "../../config";
import { Input } from "../ui/input";

const STATUS_PILL = { published: "bg-emerald-500/12 text-emerald-600", proposed: "bg-amber-500/12 text-amber-600", tba: "bg-slate-500/12 text-slate-500", cancelled: "bg-rose-500/12 text-rose-500" };

/** "Merge into…" — pick the surviving event for a duplicate. The target keeps its data, blanks are filled from the
 *  duplicate, themes united, the old slug/name become aliases (old URL keeps resolving) and the duplicate never comes back. */
export default function MergeEventDialog({ dm, authHeader, event, events, onClose, onMerged }) {
  const [suggestions, setSuggestions] = useState(null);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/travel-events/${event.id}/merge-suggestions`, { headers: authHeader() })
      .then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => setSuggestions([]));
  }, [event.id, authHeader]);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const ids = new Set((suggestions || []).map((s) => s.id));
    return (events || []).filter((e) => e.id !== event.id && !ids.has(e.id) && `${e.name} ${e.city || ""} ${e.country || ""}`.toLowerCase().includes(needle)).slice(0, 8);
  }, [q, events, event.id, suggestions]);

  const merge = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/travel-events/${event.id}/merge-into/${target.id}`, {}, { headers: authHeader() });
      toast.success(`Merged into "${data.target.name}" · ${data.filled.length} field${data.filled.length !== 1 ? "s" : ""} filled · old URL redirects`);
      onMerged?.(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Merge failed"); }
    setBusy(false);
  };

  const pill = (cls) => `px-1.5 py-0.5 rounded-full text-[10px] category-tag whitespace-nowrap ${cls}`;
  const Row = ({ e, reasons }) => (
    <button type="button" onClick={() => setTarget(e)} data-testid={`merge-target-${e.id}`}
      className={`w-full flex items-center gap-3 py-2 px-3 rounded-lg text-left transition-colors ${target?.id === e.id ? (dm ? "bg-cyan-400/15 ring-1 ring-cyan-400/40" : "bg-[#006874]/10 ring-1 ring-[#006874]/30") : (dm ? "hover:bg-white/5" : "hover:bg-[#f3f4f5]")}`}>
      <span className={`category-tag text-xs w-36 shrink-0 tabular-nums ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{e.start_date ? `${e.start_date}${e.end_date ? ` → ${e.end_date}` : ""}` : "dates TBA"}</span>
      <span className={`text-sm flex-1 min-w-0 truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{e.name}<span className={`ml-2 text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{[e.city, e.country].filter(Boolean).join(", ")}</span></span>
      {reasons?.map((r) => <span key={r} className={pill(dm ? "bg-amber-400/10 text-amber-300" : "bg-amber-50 text-amber-700")}>{r}</span>)}
      <span className={pill(STATUS_PILL[e.status || "published"])}>{e.status || "published"}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="merge-event-dialog" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`w-full max-w-2xl rounded-2xl border-2 shadow-2xl ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}>
        <div className={`flex items-start justify-between gap-3 px-6 py-4 border-b ${dm ? "border-white/6" : "border-black/5"}`}>
          <div className="min-w-0">
            <h3 className={`editorial-heading text-base font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}><GitMerge className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} /> Merge duplicate</h3>
            <p className={`category-tag text-xs mt-1 truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid="merge-source-name">“{event.name}” will be merged into the event you pick below and removed from the catalogue.</p>
          </div>
          <button onClick={onClose} data-testid="merge-dialog-close" className={`p-1.5 rounded-full ${dm ? "text-slate-400 hover:bg-white/8" : "text-[#6b7a7d] hover:bg-[#e7e8e9]"}`}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          <p className={`category-tag text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Likely duplicates</p>
          {suggestions === null && <div className="flex justify-center py-4"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>}
          {suggestions?.length === 0 && <p className={`text-xs py-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid="merge-no-suggestions">No obvious duplicate — search the catalogue below.</p>}
          <div className="space-y-1" data-testid="merge-suggestions">{(suggestions || []).map((s) => <Row key={s.id} e={s} reasons={s.reasons} />)}</div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any other event by name or city…" data-testid="merge-search-input" className={`border-0 text-sm ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
          <div className="space-y-1" data-testid="merge-search-results">{matches.map((e) => <Row key={e.id} e={e} />)}</div>
        </div>
        <div className={`flex items-center justify-between gap-3 px-6 py-4 border-t ${dm ? "border-white/6" : "border-black/5"}`}>
          <p className={`text-xs flex-1 min-w-0 truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid="merge-rule">
            {target ? <>Target <strong>{target.name}</strong> keeps its data; blanks filled, themes united, <code>/events/{event.slug}</code> → target.</> : "The target wins: it keeps every value it already has."}
          </p>
          <button onClick={merge} disabled={!target || busy} data-testid="merge-confirm-btn"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag text-sm whitespace-nowrap transition-all disabled:opacity-40 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
            {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <GitMerge className="w-3 h-3" />} Merge into target
          </button>
        </div>
      </div>
    </div>
  );
}
